import { useRecoilValue } from 'recoil';
import {
	PipelineSettingsFormState,
	SelectedMultipleDateState,
} from 'global-stores';
import { useAddDollarCurrencySurvey } from 'hooks/use-add-dollar-currency';
import { EdgesState, NodesState } from 'views/complex-onboarding-flow';
import {
	ComplexSettingFormState,
	ReminderDeadLineState,
	SendNotificationState,
} from 'views/pipeline';
import { usePublishPayload } from './publish-payload';
import { proofReadingKey } from 'views/pipelines/constants';

export const useComplexPublishAPI = () => {
	const nodes = useRecoilValue(NodesState);
	const edges = useRecoilValue(EdgesState);
	const complexSettings = useRecoilValue(ComplexSettingFormState);
	const selectedMultipleDate = useRecoilValue(SelectedMultipleDateState);
	const reminderDayList = useRecoilValue(SendNotificationState);
	const { getPayload } = usePublishPayload();
	const settingForms = useRecoilValue(PipelineSettingsFormState);

	const { modifyPagesResponse } = useAddDollarCurrencySurvey();
	// Destructure the `value` (deadline) from the ReminderDeadLineState atom, defaulting to an empty string if no value is set
	const { value: deadline = '' } = useRecoilValue(ReminderDeadLineState);

	//constants
	const { proofVerification } = proofReadingKey;

	const getConfig = (steps: any) => {
		const configsteps = steps
			?.filter((el: any) =>
				/publish|deliveryMethod|successScreen/.test(el.stepId)
			)
			?.reduce((acc: any, crr: any) => {
				return { ...acc, [crr.stepId]: crr };
			}, {});

		const updatedPayload = structuredClone(configsteps);
		const step = updatedPayload['deliveryMethod'];
		const action = step?.actions.find(
			(reminder: any) =>
				reminder?.actionId === 'reminderSetting' &&
				reminder?.metadata.reminderSettingTrigger.label === 'Custom'
		);
		if (action) {
			// Check if the reminder settings are enabled in the form settings
			if (settingForms['enableReminderSetting']) {
				// Set the reminder trigger value based on the state of reminderDayList
				action.metadata.reminderSettingTrigger.value = reminderDayList.checked
					? selectedMultipleDate || [] // If checked, use selectedMultipleDate or an empty array
					: reminderDayList.days || []; // If not checked, use reminderDayList.days or an empty array

				// Assign the selected deadline to the action metadata
				action.metadata.reminderDeadline = deadline;
			} else {
				// If reminder settings are not enabled, clear the reminder trigger and deadline values
				action.metadata.reminderSettingTrigger = '';
				action.metadata.reminderDeadline = '';
			}
		}
		return updatedPayload;
	};

	const transformConditionObj = (json: any) => {
		const { conditions = [] } = json ?? {};
		if (/form/.test(json.key)) {
			const data: any = [];
			const outputObj: any = {};
			conditions.forEach((item: any) => {
				const labelId = item.labelId;
				if (labelId in outputObj) {
					outputObj[labelId].push({
						label: item.label,
						key: item.key,
						nodeId: item.nodeId,
					});
				} else {
					outputObj[labelId] = [
						{
							label: item.label,
							key: item.key,
							nodeId: item.nodeId,
						},
					];
				}
			});
			data.push(outputObj);
			return data ?? [];
		}
		if (json.key === 'dynamicForm') {
			return conditions;
		}
		if (/amlVerification|kybVerification/.test(json.key)) {
			const data = [];
			for (let i = 0; i < conditions.length; i++) {
				const payload = {
					[conditions[i]['and'] ? 'and' : 'or']:
						conditions[i]['and'] ?? conditions[i]['or'],
					then: conditions[i].then,
					nodeId: conditions[i].nodeId,
					key: conditions[i].key,
				};

				data.push(payload);
			}
			return data;
		}
		if (json.key === 'allId') {
			const data = [];

			for (let i = 0; i < conditions.length; i++) {
				const payload = {
					[conditions[i]['and'] ? 'and' : 'or']:
						conditions[i]['and'] ?? conditions[i]['or'],
					then: conditions[i].then,
					nodeId: conditions[i].nodeId,
					key: conditions[i].key,
				};
				const datas = {
					[conditions[i].labelId]: payload,
				};

				data.push(datas);
			}

			return data;
		} else {
			return conditions;
		}
	};

	const deleteNodesValue = (dupNodes: any, i: number) => {
		delete dupNodes[i].height;
		delete dupNodes[i].dragging;
		delete dupNodes[i].data;
		delete dupNodes[i].updateStyle;
		delete dupNodes[i].width;
		delete dupNodes[i].zIndex;
		delete dupNodes[i].selected;
		delete dupNodes[i].positionAbsolute;
		delete dupNodes[i].position;
		delete dupNodes[i].type;
		delete dupNodes[i]?.formMetaData;
		// delete dupNodes[i].key;
		delete dupNodes[i].sourceHandle;
	};

	const getMetData = (metaData: any, key: string) => {
		const newObj = structuredClone(metaData);
		if (!newObj) {
			return {};
		}

		if (key === 'kybForm') {
			const data = [];

			const { elements } = structuredClone(newObj) ?? [];
			for (let i = 0; i < elements.length; i++) {
				const choices = [];
				for (let j = 0; j < elements[i]?.choices.length; j++) {
					delete elements[i].choices[j].status;
					choices.push(elements[i].choices[j]);
				}
				delete elements[i].status;
				data.push({
					...elements[i],
					choices,
				});
			}
			return { questions: data };
		}
		if (key === 'accreditationVerify') {
			// saeeting undefind for  imported , configured key as accreditation 506b  accreditationVerify custom upload has this key
			delete newObj.accreditationType?.configured;
			delete newObj.accreditationType?.imported;
		}

		return newObj;
	};

	const getAction = (obj: any, index: number) => {
		const { key, id } = obj ?? {};
		if (complexSettings[id] && key !== 'dynamicForm') {
			const payload = {
				actionId: key,
				metadata: getMetData(complexSettings[id], key),
				index,
			};
			return [payload];
		}

		if (key === proofVerification) {
			const payload = {
				actionId: key,
				metadata: {
					proofReading: 'proofReading',
				},
				index: 0,
			};
			return [payload];
		}

		if (key === 'amlVerification') {
			return [{ actionId: key, index, metadata: {} }];
		}

		// Basic Info is presence-only — emit a stable action so the node carries
		// its actionId (like every other step) even without configurable metadata.
		if (key === 'basicInfo') {
			return [{ actionId: key, index, metadata: {} }];
		}

		// Second Contact is presence-only too — same stable single action.
		if (key === 'secondContact') {
			return [{ actionId: key, index, metadata: {} }];
		}

		if (key === 'dynamicForm') {
			const metaFormData = structuredClone(complexSettings[id]);
			const cloneMetaData = {
				...metaFormData,
				pages: modifyPagesResponse(metaFormData?.pages ?? []),
			};
			return [
				{
					actionId: key,
					index,
					metadata: {
						questions: cloneMetaData,
					},
				},
			];
		}

		return [];
	};

	const getComplexPayload = (authId?: string) => {
		const data = getPayload();
		const dupNodes = JSON.parse(JSON.stringify(nodes));

		dupNodes.forEach((node: any) => {
			if (node.edgeId) {
				delete node.edgeId;
			}
		});
		
		for (let i = 0; i < dupNodes.length; i++) {
			dupNodes[i]['actions'] = getAction(dupNodes[i], i);
			dupNodes[i]['conditions'] = transformConditionObj(dupNodes[i]);
			deleteNodesValue(dupNodes, i);
		}

		// __simplici_kyc_aml_single_tile__: KYC and AML are ONE tile on the canvas,
		// but they CANNOT be one node in the published flow: pipelines.api dispatches
		// on stepId (switch over STEPS.KYC / STEPS.AML in complex-sessions), so an
		// amlVerification action carried on a kyc-stepId node would publish and price
		// but never run. So the AML step is synthesised here instead.
		//
		// It is spliced BETWEEN the KYC node and whatever followed it -- parentId is
		// how this payload expresses the graph -- so KYC runs first and PEPs &
		// Sanctions second, which is the whole point of merging them.
		//
		// `design` is deliberately NOT touched: that is the canvas view, and it must
		// keep showing the single KYC tile the user placed.
		const amlSourceNodes = dupNodes.filter(
			(node: any) =>
				node?.stepId === 'kyc' &&
				complexSettings?.[node?.id]?.amlVerification === true
		);
		amlSourceNodes.forEach((kycNode: any) => {
			const amlId = `aml-${kycNode.id}`;
			// Anything that pointed at KYC now points at AML, so the new step lands
			// between them rather than as a second branch off KYC.
			dupNodes.forEach((node: any) => {
				if (!Array.isArray(node?.parentId)) return;
				if (node.id === amlId) return;
				node.parentId = node.parentId.map((pid: any) =>
					pid === kycNode.id ? amlId : pid
				);
			});
			const amlNode = {
				id: amlId,
				key: 'amlVerification',
				stepId: 'aml',
				label: 'AML (Anti-Money Laundering)',
				parentId: [kycNode.id],
				conditions: [],
				actions: [{ actionId: 'amlVerification', index: 0, metadata: {} }],
			};
			const at = dupNodes.findIndex((node: any) => node.id === kycNode.id);
			dupNodes.splice(at + 1, 0, amlNode);
		});

		const isBiometric = nodes.find((el: any) => el.stepId === 'authentication');
		// LIQ2-100 FR-11b (flexible starts): basicInfo, secondContact, and biometric authentication
		// are all valid pipeline entry points. Only inject the synthetic authentication root when the
		// graph has NONE of them as a root — so a basicInfo/secondContact-rooted pipeline is left as
		// authored. Pipelines with neither still get the legacy auth root (back-compat).
		const hasBasicInfoRoot = dupNodes.some(
			(el: any) =>
				(el.stepId === 'basicInfo' || el.stepId === 'secondContact') &&
				!el.parentId?.length
		);

		// LIQ2-100 FR-1/FR-2: a CHILD pipeline (parentPipelineId set) is a downstream stage —
		// its entry node is whatever the author chose (e.g. KYC), so never force an auth root on it.
		// The chain root is the only pipeline bound by the start-node rule (enforced backend-side).
		const isChildPipeline = !!data?.parentPipelineId;

		if (!isChildPipeline && !isBiometric && !hasBasicInfoRoot) {
			const parentNode = dupNodes.findIndex((el: any) => !el.parentId?.length);
			if (parentNode !== -1) {
				dupNodes[parentNode]['parentId'] = authId ? [authId] : ['face-auth-0'];
			}

			const findAuth = data?.steps.find(
				(el: any) => el.stepId === 'authentication'
			);

			//deleting index as this is not required
			delete findAuth?.index;

			const faceAuth = {
				...findAuth,
				label: 'Authentication',
				id: authId || 'face-auth-0',
				parentId: [],
			};
			dupNodes.unshift(faceAuth);
		}

		const payload = {
			name: data.name,
			configurations: getConfig(data.steps) ?? {},
			nodes: JSON.parse(JSON.stringify(dupNodes ?? [])),
			design: {
				nodes,
				edges,
			},
			// LIQ2-100 FR-1/FR-2 (additive): user-defined pipeline slug + parent-pipeline reference.
			// Sourced from the settings form; omitted when unset so existing saves are unchanged.
			...(data?.slug ? { slug: data.slug } : {}),
			...(data?.parentPipelineId
				? { parentPipelineId: data.parentPipelineId }
				: {}),
		};

		return payload;
	};

	return { getComplexPayload };
};
